import React from "react";
import { UIAction } from "../types";
import { getActionDisplayLabel } from "../utils/actionHandlers";
import { StarterActionIcon } from "./StarterActionIcon";

export interface NewChatWelcomeSuggestionsProps {
  actions: UIAction[];
  isLoading: boolean;
  onActionClick: (action: UIAction) => void;
  accentPillClass: string;
  neutralPillClass: string;
  mode?: "cards" | "chips" | "all";
  /** One more card in the same row, for an agent-specific starter (Material icon name). */
  extraCard?: { label: string; icon: string; onClick: () => void };
}

/**
 * Empty-state starter chips only (not shared with in-thread ActionButtons).
 * Layout/spacing are tuned for the new-chat pane, not message bubbles.
 */
export const NewChatWelcomeSuggestions: React.FC<
  NewChatWelcomeSuggestionsProps
> = ({
  actions,
  isLoading,
  onActionClick,
  accentPillClass,
  neutralPillClass,
  mode = "all",
  extraCard,
}) => {
  const cardActions = actions.slice(0, 3);
  const chipActions = actions.slice(3);

  const showCards = (mode === "cards" || mode === "all") && (cardActions.length > 0 || !!extraCard);
  const showChips = (mode === "chips" || mode === "all") && chipActions.length > 0;

  return (
    <div className="flex w-full flex-col items-center space-y-5">
      {showCards && (
        <div className="flex w-full flex-wrap justify-center gap-2.5 sm:gap-3">
          {cardActions.map((action, idx) => {
            const label = getActionDisplayLabel(action);
            return (
              <button
                key={`${action.type}-${idx}`}
                type="button"
                onClick={() => onActionClick(action)}
                disabled={isLoading}
                className="group flex min-h-[92px] w-full max-w-[260px] flex-none flex-col items-start justify-start rounded-xl border border-theme-border bg-theme-surface/80 px-3 py-3 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md disabled:opacity-50 sm:w-[232px] sm:max-w-none"
              >
                <span className="mb-2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-theme-secondary/20 text-theme-primary">
                  <StarterActionIcon type={action.type} />
                </span>
                <span className="text-sm font-medium leading-snug text-theme-fg">
                  {label}
                </span>
                <span className="mt-1 text-[11px] leading-snug text-theme-fg-muted">
                  Choose this to get started quickly.
                </span>
              </button>
            );
          })}
          {extraCard && (
            <button
              type="button"
              onClick={extraCard.onClick}
              disabled={isLoading}
              className="group flex min-h-[92px] w-full max-w-[260px] flex-none flex-col items-start justify-start rounded-xl border border-theme-border bg-theme-surface/80 px-3 py-3 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md disabled:opacity-50 sm:w-[232px] sm:max-w-none"
            >
              <span className="mb-2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-theme-secondary/20 text-theme-primary">
                <span aria-hidden="true" className="material-icons text-[16px]">{extraCard.icon}</span>
              </span>
              <span className="text-sm font-medium leading-snug text-theme-fg">
                {extraCard.label}
              </span>
              <span className="mt-1 text-[11px] leading-snug text-theme-fg-muted">
                Choose this to get started quickly.
              </span>
            </button>
          )}
        </div>
      )}

      {showChips && (
        <div className="flex w-full flex-wrap justify-center gap-2">
          {chipActions.map((action, idx) => {
            const label = getActionDisplayLabel(action);
            return (
              <button
                key={`${action.type}-${idx}`}
                type="button"
                onClick={() => onActionClick(action)}
                disabled={isLoading}
                className={`${neutralPillClass} new-chat-starter-pill border-theme-border bg-theme-surface/80 text-theme-fg`}
              >
                <StarterActionIcon type={action.type} />
                <span className="text-balance">{label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
